import React from 'react';
import { MapPin, Wrench, Camera, Building2, GraduationCap, Dumbbell, Home } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { workGallery } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';

const caseStudies = [
  {
    id: 8,
    title: "Fujifilm House of Photography",
    year: "2026",
    location: "Auckland",
    scope: [
      "Maglocks and electronic mortice locks installed throughout for the new security system"
    ],
    category: "Commercial",
    image: "/images/projects/fujifilm-house-of-photography.png",
    imageAlt: "Fujifilm House of Photography — maglocks and electronic mortice locks by Shore Doors"
  },
  {
    id: 1,
    title: "North Shore Hospital",
    year: "2023",
    location: "Auckland",
    scope: [
      "Installed maglocks across the site"
    ],
    category: "Healthcare",
    image: "/images/projects/north-shore-hospital.webp",
    imageAlt: "North Shore Hospital — 2023 build"
  },
  {
    id: 2,
    title: "Auckland City Hospital",
    year: null,
    location: "Auckland",
    scope: [
      "Access control hardware and door hardware installations across multiple areas"
    ],
    category: "Healthcare",
    image: "/images/projects/auckland-city-hospital.webp",
    imageAlt: "Auckland City Hospital"
  },
  {
    id: 3,
    title: "Manukau Super Clinic",
    year: null,
    location: "Auckland",
    scope: [
      "Installed access-controlled locks including V-locks, mortice locks, and maglocks"
    ],
    category: "Healthcare",
    image: "/images/projects/manukau-super-clinic.avif",
    imageAlt: "Manukau Super Clinic"
  },
  {
    id: 4,
    title: "BNZ Head Office, Queen Street",
    year: null,
    location: "Auckland CBD",
    scope: [
      "Commercial door hardware and access control installation"
    ],
    category: "Corporate",
    image: "/images/projects/bnz-queen-street.webp",
    imageAlt: "BNZ Head Office, Queen Street"
  },
  {
    id: 5,
    title: "Pukekohe Train Station",
    year: null,
    location: "Pukekohe, Auckland",
    scope: [
      "Installed mechanical and electronic door hardware",
      "Installed door automation devices"
    ],
    category: "Transport / Government",
    image: "/images/projects/pukekohe-train-station.webp",
    imageAlt: "Pukekohe Train Station"
  },
  {
    id: 6,
    title: "Papakura Library",
    year: null,
    location: "Auckland",
    scope: [
      "Commercial door hardware and access control installation"
    ],
    category: "Community / Government",
    image: "/images/projects/papakura-library.webp",
    imageAlt: "Papakura Library"
  },
  {
    id: 7,
    title: "South Auckland Animal Shelter",
    year: null,
    location: "South Auckland",
    scope: [
      "Commercial door hardware and access control installation"
    ],
    category: "Community",
    image: "/images/projects/spca-south-auckland.jpg",
    imageAlt: "South Auckland Animal Shelter (SPCA)"
  }
];

const partnerSectors = [
  {
    icon: GraduationCap,
    title: "Schools",
    description: "Electronic and mechanical lock hardware installations for primary, intermediate, and secondary schools across Auckland."
  },
  {
    icon: Building2,
    title: "Libraries",
    description: "Door hardware and access control component installations for public and community library facilities."
  },
  {
    icon: Dumbbell,
    title: "Gyms",
    description: "Access-controlled entry hardware and door automation for fitness and recreation facilities."
  },
  {
    icon: Home,
    title: "Retirement Villages",
    description: "Secure door hardware installations balancing safety requirements with resident independence."
  }
];

const Gallery = () => {
  usePageMeta({
    title: 'Completed Projects | Shore Doors Locksmiths',
    description:
      'Real installations across Auckland — North Shore Hospital, BNZ Queen Street, Pukekohe Train Station and more. Door hardware and access control delivered properly.',
  });
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gray-950 py-16 sm:py-20 lg:py-24 text-white overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src="/images/hero-install.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 from-25% via-gray-950/75 via-55% to-gray-950/5"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="gallery-heading">
              Completed Projects
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300">
              Real installations delivered across Auckland for integrators, builders, and facilities teams
            </p>
          </div>
        </div>
      </section>

      {/* Featured Case Studies */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="case-studies-heading">
              Featured Installations
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A selection of completed commercial projects
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8" data-testid="case-studies-grid">
            {caseStudies.map((project) => (
              <Card key={project.id} className="border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300" data-testid={`case-study-${project.id}`}>
                {/* Project image or placeholder */}
                <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.imageAlt || project.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-center">
                      <Camera className="text-gray-400 mx-auto mb-2" size={32} />
                      <p className="text-xs text-gray-400 font-medium">Project imagery coming soon</p>
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {project.category}
                  </span>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{project.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {project.location}
                    </span>
                    {project.year && (
                      <span className="text-gray-400">{project.year}</span>
                    )}
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                      <Wrench size={14} className="text-blue-600" />
                      Installation Scope
                    </h4>
                    <ul className="space-y-1.5">
                      {project.scope.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[9px] before:w-2 before:h-2 before:bg-blue-200 before:rounded-full">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Recent work — real install photos */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="work-gallery-heading">
              On the tools
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Straight off the job — locks and hardware our team has installed around Auckland
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="work-gallery-grid">
            {workGallery.map((photo) => (
              <figure key={photo.src} className="group relative rounded-xl overflow-hidden bg-gray-200">
                <img
                  src={photo.src}
                  alt={photo.caption}
                  loading="lazy"
                  className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-900/85 to-transparent px-3 pt-8 pb-2.5">
                  <span className="text-white text-xs leading-snug">{photo.caption}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Partner-Delivered Projects */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="partner-projects-heading">
              Additional Commercial Projects Delivered via Partners
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We regularly deliver hardware installations across these sectors, working alongside access control integrators, builders, and facilities management teams
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="partner-sectors-grid">
            {partnerSectors.map((sector, idx) => (
              <Card key={idx} className="border-gray-200 hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <sector.icon className="text-blue-600" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{sector.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{sector.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
